import axios, { AxiosInstance } from 'axios';
import authEvents from '@shared/services/auth-events';
import MCPMarketEnvironmentDetection from '@src/shared/utils/market-environment-detection';
import keycloak from '@src/keycloak';
abstract class HttpClient {
  protected readonly instance: AxiosInstance;
  private authHeaderName: string;
  private isBehindKeyCloak: boolean;
  private predefinedToken: string;
  constructor(
    baseURL: string,
    authHeaderName: string = 'Authorization',
    isBehindKeyCloak: boolean = true,
    predefinedToken: string = ''
  ) {
    this.instance = axios.create({
      baseURL,
    });

    const getConfigWithAuthHeader = () => {
      const headerValue = this.instance.defaults.headers.common[authHeaderName];
      const tokenPrefix = this.isBehindKeyCloak ? 'Bearer ' : '';

      if (predefinedToken) {
        return `${tokenPrefix}${predefinedToken}`;
      }

      if (headerValue) {
        return `${tokenPrefix}${headerValue}`;
      }

      if (keycloak.authenticated && keycloak.token) {
        return `${tokenPrefix}${keycloak.token}`;
      }

      return null;
    };

    this.instance.interceptors.response.use(
      (res) => res.data,
      (error) => {
        return Promise.reject(error);
      }
    );

    this.instance.interceptors.request.use(
      (config) => {
        if (isBehindKeyCloak) {
          return new Promise((resolve, reject) => {
            const configHeader = getConfigWithAuthHeader();
            if (configHeader) {
              config.headers[authHeaderName] = configHeader;
              resolve(config);
            } else {
              authEvents.subscribeToKeycloakToken((token) => {
                config.headers[authHeaderName] = `${
                  this.isBehindKeyCloak ? 'Bearer ' : ''
                }${token}`;
                resolve(config);
              });
            }
          });
        } else {
          config.headers[authHeaderName] = predefinedToken;
        }

        return Promise.resolve(config);
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    this.isBehindKeyCloak = isBehindKeyCloak;
    this.authHeaderName = authHeaderName;
    this.predefinedToken = predefinedToken;

    if (isBehindKeyCloak) {
      const { countryCode } = MCPMarketEnvironmentDetection.getMarketEnvironment();
      this.addHeader('Country', countryCode);
    }
  }

  public addHeader = (headerName: string, headerValue: string) => {
    this.instance.defaults.headers.common[headerName] = headerValue;
  };

  public removeHeader = (headerName: string) => {
    this.instance.defaults.headers.common[headerName] = '';
  };

  public addAuthHeader = (token: string) => {
    const tokenPrefix = this.isBehindKeyCloak ? 'Bearer ' : '';
    this.instance.defaults.headers.common[this.authHeaderName] = `${tokenPrefix}${token}`;
  };

  public removeAuthHeader = () => {
    this.instance.defaults.headers.common[this.authHeaderName] = '';
  };
}

export default HttpClient;
